import { useState } from "react"
import { useTranslation } from "react-i18next"
import { NavLink, useNavigate } from "react-router-dom"
import {
    useGetAbsenceStatsQuery,
    useGetPendingAbsenceRequestsQuery,
    useAcceptRequestMutation,
    useDeclineRequestMutation,
} from "../api/absenceReportApiSlice"
import secondsToHms from "../../../../utility/SecondToHms"
import AvatarMini from "components/commons/AvatarMini"
import Loading from "components/commons/Loading"
import Modal from "components/commons/Modal"
import UserImage from "components/commons/UserImage"
import HasPermission, { CanManageAbsenceRequest } from "app/PermissionChecker"

const PERIOD_TODAY = "today"
const PERIOD_TOMORROW = "tomorrow"
const PERIOD_WEEK = "week"

function getRangeForPeriod(period) {
    const start = new Date()
    const end = new Date()

    if (period === PERIOD_TOMORROW) {
        start.setDate(start.getDate() + 1)
        end.setDate(end.getDate() + 1)
    } else if (period === PERIOD_WEEK) {
        end.setDate(end.getDate() + 6)
    }

    start.setHours(0, 0, 0, 0)
    end.setHours(23, 59, 59, 0)

    return { start: start.getTime() / 1000, end: end.getTime() / 1000 }
}

function groupByType(absences) {
    return absences.reduce((acc, a) => {
        const key = a.type
        if (!acc[key]) acc[key] = []
        acc[key].push(a)
        return acc
    }, {})
}

const TYPE_CONFIG = {
    HOLIDAY: { icon: "fa-solid fa-umbrella-beach", labelKey: "absenceReport:entity.absence.reason.holidays" },
    SICK: { icon: "fa-solid fa-house-chimney-medical", labelKey: "absenceReport:entity.absence.reason.sick" },
}

function absenceTypeIcon(type) {
    switch (type) {
        case "HOLIDAY": return <i className="fa-solid fa-umbrella-beach" />
        case "SICK": return <i className="fa-solid fa-house-chimney-medical" />
        case "PERMIT": return <i className="fa-solid fa-clock" />
        default: return null
    }
}

function absenceDateLabel(req) {
    if (req.type === "PERMIT") {
        const date = new Date(req.startDate * 1000).toLocaleDateString()
        const from = new Date(req.startDate * 1000).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
        const to = new Date(req.endDate * 1000).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
        return `${date} ${from}–${to}`
    }
    const from = new Date(req.startDate * 1000).toLocaleDateString()
    const to = new Date(req.endDate * 1000).toLocaleDateString()
    return from === to ? from : `${from} – ${to}`
}

function AbsenceManagerWidget() {
    const [t] = useTranslation(["global", "absenceReport"])
    const canManageAbsenceRequest = HasPermission(CanManageAbsenceRequest)
    const navigate = useNavigate()
    const [period, setPeriod] = useState(PERIOD_TODAY)

    const { start, end } = getRangeForPeriod(period)
    const showDate = period === PERIOD_WEEK

    const { data: absenceStats, isSuccess: statsOk, isFetching: statsFetching } = useGetAbsenceStatsQuery({ start, end })
    const { data: pendingRequests, isSuccess: pendingOk, isFetching: pendingFetching } = useGetPendingAbsenceRequestsQuery()

    const [acceptRequest] = useAcceptRequestMutation()
    const [declineRequest] = useDeclineRequestMutation()

    const isFetching = statsFetching || pendingFetching

    const allAbsences = statsOk ? absenceStats.absences : []
    const absentsToday = allAbsences.filter(a => a.type !== "PERMIT")
    const permitsToday = allAbsences.filter(a => a.type === "PERMIT")
    const totalPermitSeconds = permitsToday.reduce((sum, a) => sum + (a.hours ?? 0), 0)
    const pendingCount = pendingOk ? pendingRequests.length : null
    const groupedAbsents = groupByType(absentsToday)

    return (
        <div className="card bg-black bg-opacity-10 text-white mb-4">
            <div className="card-body">

                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4 className="mb-0 text-muted">
                        <i className="fa-solid fa-user-clock" /> {t("absenceReport:menu.absences")}
                    </h4>
                    <div className="d-flex align-items-center gap-2">
                        {isFetching && <Loading />}
                        <select
                            className="form-select w-auto"
                            value={period}
                            onChange={(e) => setPeriod(e.target.value)}
                        >
                            <option value={PERIOD_TODAY}>{t("time.today")}</option>
                            <option value={PERIOD_TOMORROW}>{t("time.tomorrow")}</option>
                            <option value={PERIOD_WEEK}>{t("time.next_n_days", { days: 7 })}</option>
                        </select>
                        <NavLink to="/dashboard/absences/dashboard" className="btn btn-sm btn-outline-secondary">
                            <i className="fa-solid fa-arrow-right" />
                        </NavLink>
                    </div>
                </div>

                <div className={`row g-3 ${isFetching ? "opacity-25" : ""}`}>

                    {canManageAbsenceRequest &&/* Richieste in attesa */
                        <div className="col-12 col-md-4">
                            <div className="card bg-black bg-opacity-10 text-bg-light">
                                <div className="card-body">
                                    <h6 className="card-subtitle mb-2 text-muted text-center">
                                        <i className="fa-solid fa-hourglass-half" /> {t("absenceReport:menu.requests_pending")}
                                    </h6>
                                    <h3 className={`text-center ${pendingCount > 0 ? "text-warning" : ""}`}>
                                        {pendingCount !== null ? pendingCount : "—"}
                                    </h3>

                                    {pendingOk && pendingRequests.length > 0 && (
                                        <div className="mt-2">
                                            {pendingRequests.map((req, i) => (
                                                <div key={req.guid} className={`d-flex align-items-center justify-content-between gap-2 py-2 ${i > 0 ? "border-top border-secondary" : ""}`}>
                                                    <div className="d-flex align-items-center gap-2 overflow-hidden">
                                                        {/* <AvatarMini abbreviate={true} guid={req.user_guid} displayName={req.userDisplayName} /> */}

                                                        <div className="col-auto">
                                                            <UserImage guid={req.user_guid} size={32} />
                                                        </div>
                                                        <div className="col">
                                                            <div className="overflow-hidden">
                                                                <div className="small fw-semibold text-truncate">  {req.userDisplayName}</div>
                                                                <small className="text-muted">
                                                                    {absenceTypeIcon(req.type)} {absenceDateLabel(req)}
                                                                </small>
                                                            </div>

                                                        </div>
                                                    </div>
                                                    <div className="d-flex gap-1 flex-shrink-0">
                                                        <Modal
                                                            modalId={`widget_accept_${req.guid}`}
                                                            buttonClass="btn-success"
                                                            buttonIcon={<i className="fa-solid fa-check" />}
                                                            buttonLabel={t("action.accept")}
                                                            iconOnly={true}
                                                            body={t("absenceReport:helper.confirm_accept")}
                                                            onConfirm={() => acceptRequest(req.guid)}
                                                        />
                                                        <Modal
                                                            modalId={`widget_deny_${req.guid}`}
                                                            buttonClass="btn-danger"
                                                            buttonIcon={<i className="fa-solid fa-xmark" />}
                                                            buttonLabel={t("action.deny")}

                                                            iconOnly={true}
                                                            body={t("absenceReport:helper.confirm_deny")}
                                                            onConfirm={() => declineRequest(req.guid)}
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    }
                    {/* Assenti — raggruppati per tipo */}
                    <div className="col-12 col-md-4">
                        <div className="card bg-black bg-opacity-10 text-bg-light">
                            <div className="card-body text-center">
                                <h6 className="card-subtitle mb-2 text-muted">
                                    <i className="fa-solid fa-users" /> {t("absenceReport:entity.absents")}
                                </h6>
                                <h3>{statsOk ? absentsToday.length : "—"}</h3>
                                {Object.entries(groupedAbsents).map(([type, entries]) => {
                                    const cfg = TYPE_CONFIG[type]
                                    return (
                                        <div key={type} className="text-start mt-2">
                                            <small className="text-muted text-uppercase fw-semibold">
                                                {cfg && <i className={`${cfg.icon} me-1`} />}
                                                {cfg ? t(cfg.labelKey) : type}
                                            </small>
                                            {entries.map((a, i) => (
                                                <div key={i} className="mb-1">
                                                    <AvatarMini size={32} abbreviate={true} guid={a.userGuid} displayName={a.userDisplayName} />
                                                    {showDate && a.startDate && (
                                                        <small className="text-muted ms-1">
                                                            {new Date(a.startDate * 1000).toLocaleDateString()}
                                                            {a.endDate && a.endDate !== a.startDate &&
                                                                <> – {new Date(a.endDate * 1000).toLocaleDateString()}</>
                                                            }
                                                        </small>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Permessi */}
                    <div className="col-12 col-md-4">
                        <div className="card bg-black bg-opacity-10 text-bg-light">
                            <div className="card-body text-center">
                                <h6 className="card-subtitle mb-2 text-muted">
                                    <i className="fa-solid fa-user-clock" /> {t("absenceReport:entity.permitts")}
                                </h6>
                                <h3>{statsOk ? (totalPermitSeconds > 0 ? secondsToHms(totalPermitSeconds) : "0 h") : "—"}</h3>
                                {permitsToday.length > 0 && (
                                    <div className="text-start mt-2">
                                        {permitsToday.map((a, i) => (
                                            <div key={i} className="mb-1">
                                                <AvatarMini abbreviate={true} guid={a.userGuid} displayName={a.userDisplayName} />
                                                <small className="text-muted ms-1">
                                                    {secondsToHms(a.hours ?? 0)}
                                                    {showDate && a.startDate && (
                                                        <> &middot; {new Date(a.startDate * 1000).toLocaleDateString()}</>
                                                    )}
                                                </small>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    )
}

export default AbsenceManagerWidget
